import { Select, Table } from 'antd'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { PerEmpty } from '../../../components/custom_components/Empty'
import { FormikSelect, FormItem, InputNumber } from '../../../components/formik-component'
import { useAuth } from '../../../context/auth-context'
import { Country, PyramidType, ReferencePyramidDetail } from '../../../types'
import { LimitType } from './PyramidLimitType.enum'
import { isR3Pyramid, isR2Pyramid } from '../pyramidUtils'
const { Option } = Select

interface Props {
  type: PyramidType
  formikValues: ReferencePyramidDetail[]
  country: Country
}

export const EditablePyramidsTable: FC<Props> = ({ type, formikValues, country }) => {
  const [t] = useTranslation()
  const { user } = useAuth()

  const getLimitTypeNameAndValue = (reference: ReferencePyramidDetail, index: number) => {
    if ((isR3Pyramid(country) || isR2Pyramid(country)) && type === PyramidType.Level) {
      if (reference.range === 'C2') {
        const c1index = formikValues.findIndex((el) => el.range === 'C1')
        return { name: `${c1index}.limitType`, value: formikValues[c1index].limitType }
      }
      if (reference.range === 'M') {
        const prIndex = formikValues.findIndex((el) => el.range === 'PR')
        return { name: `${prIndex}.limitType`, value: formikValues[prIndex].limitType }
      }
      if (reference.range === 'SM') {
        const sprIndex = formikValues.findIndex((el) => el.range === 'SPR')
        return { name: `${sprIndex}.limitType`, value: formikValues[sprIndex].limitType }
      }
      return { name: `${index}.limitType`, value: formikValues[index].limitType }
    } else {
      if (reference.range === 'C2') {
        const c1index = formikValues.findIndex((el) => el.range === 'C1')
        return { name: `${c1index}.limitType`, value: formikValues[c1index].limitType }
      }
      return { name: `${index}.limitType`, value: formikValues[index].limitType }
    }
  }

  const columns = [
    {
      key: 'pyramids-range',
      title: t(`pyramidsView.type.${type}`),
      className: type === PyramidType.Level ? 'textAlign-left' : 'textAlign-center',
      render: (tableData: ReferencePyramidDetail) =>
        type === PyramidType.Level ? t(`employee.contract.level.${tableData.range}`) : tableData.range,
    },
    {
      key: 'pyramids-limit',
      title: t('pyramidsView.shared.overview.minMax'),
      className: 'textAlign-center',
      render: (tableDataFormik: ReferencePyramidDetail, _record: ReferencePyramidDetail, index: number) => {
        const { name, value } = getLimitTypeNameAndValue(tableDataFormik, index)
        return (
          <FormikSelect placeholder={''} style={{ width: '100%' }} optionFilterProp="children" size="small" value={value} name={name}>
            <Option value={LimitType.Min}>{t('pyramidsView.shared.overview.Min')}</Option>
            <Option value={LimitType.Max}>{t('pyramidsView.shared.overview.Max')}</Option>
          </FormikSelect>
        )
      },
    },
    {
      key: 'pyramids-recommended',
      title: t(`pyramidsView.shared.overview.heading`),
      className: 'textAlign-center',
      width: '100px',
      render: (_tableDataFormik: ReferencePyramidDetail, _record: ReferencePyramidDetail, index: number) => {
        return (
          <FormItem name={`${index}.percentage`}>
            <InputNumber
              name={`${index}.percentage`}
              type="percentage"
              value={formikValues[index].percentage}
              timezoneAndLocale={user}
              min={0}
              max={100}
              step={1}
              style={{ width: 100 }}
            />
          </FormItem>
        )
      },
    },
  ]

  return (
    <Table
      dataSource={formikValues.map((pyramidEntry) => ({ ...pyramidEntry, key: pyramidEntry.id }))}
      columns={columns}
      pagination={false}
      locale={{
        emptyText: <PerEmpty heading={t('table.emptyText')} icon />,
      }}
    />
  )
}
